import {Select, Chip, FormControl, IconButton, Input, MenuItem, InputAdornment} from '@mui/material'
import {SelectChangeEvent} from '@mui/material/Select'
import * as React from 'react'
import {ReactNode} from 'react'
import {AppAdminContext} from '../../../app/AppAdminContext'
import CloseIcon from '@mui/icons-material/Close'
import withStyles from '@mui/styles/withStyles'
import styled from 'styled-components'
import {LeftColumnFlexContainer} from '../../../styledComponents/FlexComponents'

export interface OptionProps {
    label: string
    value: string
}

interface Props {
    options: OptionProps[]
    placeholder?: string
    onChange: (selected: string[]) => void
    defaultValue?: string[],
}

interface State {
    selectedOption: OptionProps[]
}

class PPMultiSelect
    extends React.Component<Props, State> {

    static displayName: string = 'PPMultiSelect'

    constructor(props: Readonly<Props>) {
        super(props)
        this.state = {
            selectedOption: this.props.defaultValue ? this.getSelectedOption(this.props.defaultValue) : []
        }
        this.handleChange = this.handleChange.bind(this)
        this.handleDeleteChip = this.handleDeleteChip.bind(this)
        this.handleClearAll = this.handleClearAll.bind(this)
        this.renderChips = this.renderChips.bind(this)
    }

    getSelectedOption(values: string[]): OptionProps[] {
        return values.reduce<OptionProps[]>((result, id) => {
            const target = this.props.options.find((item) => item.value === id)
            if (target) {
                result.push(target)
            }
            return result
        }, [])
    }

    handleChange(event: SelectChangeEvent<string[]>): void {
        this.setState({selectedOption: this.getSelectedOption(event.target.value as string[])})

        if (this.props.onChange) {
            this.props.onChange(event.target.value as string[])
        }
    }

    handleDeleteChip(valueToDelete: string): void {
        const selectedOption = this.state.selectedOption
            .filter((option) => option.value !== valueToDelete)
        this.setState({selectedOption: selectedOption})

        if (this.props.onChange) {
            this.props.onChange(selectedOption.map(option => option.value))
        }
    }

    handleClearAll(): void {
        this.setState({selectedOption: []})
        if (this.props.onChange) {
            this.props.onChange([])
        }
    }

    renderChips(): ReactNode {
        if (this.state.selectedOption.length === 0) {
            return (<PlaceHolderText>{this.props.placeholder}</PlaceHolderText>)
        }
        return (
            <ChipsContainer>
                {(this.state.selectedOption).map((option) => (
                    <CustomChip
                        key={option.value}
                        label={option.label}
                        onDelete={() => this.handleDeleteChip(option.value)}
                        deleteIcon={<CloseIcon/>}
                        onMouseDown={(e) => e.stopPropagation()}
                        size={'small'}
                    />
                ))}
            </ChipsContainer>
        )
    }

    render(): ReactNode {
        const customInput = (
            <CustomInput
                endAdornment={
                    this.state.selectedOption.length > 0 ?
                        <InputAdornment position="end">
                            <IconButton
                                onClick={this.handleClearAll}
                                size={'small'}
                                style={{marginRight: 24}}>
                                <CloseIcon fontSize="small"/>
                            </IconButton>
                        </InputAdornment> : null
                }
            />
        )

        return <>
            <LeftColumnFlexContainer>
                <CustomFormControl>
                    <Select
                        multiple
                        displayEmpty
                        input={customInput}
                        value={this.state.selectedOption.map(o => o.value)}
                        onChange={this.handleChange}
                        renderValue={this.renderChips}
                        MenuProps={{
                            anchorOrigin: {
                                vertical: 'bottom',
                                horizontal: 'left'
                            },
                            transformOrigin: {
                                vertical: 'top',
                                horizontal: 'left'
                            },
                            PaperProps: {
                                style: {maxHeight: 300}
                            }
                        }}
                        variant={'standard'}
                    >
                        {this.props.options.map((item) => {
                            return (
                                <MenuItem key={item.value} value={item.value}>
                                    {item.label}
                                </MenuItem>)
                        })}
                    </Select>
                </CustomFormControl>
            </LeftColumnFlexContainer>
        </>
    }
}

PPMultiSelect.contextType = AppAdminContext
export default PPMultiSelect

const CustomChip = withStyles({
    root: {
        margin: 2,
        borderRadius: 4,
        height: 24,
        fontSize: '0.85rem',
        paddingRight: 4,
        whiteSpace: 'nowrap'
    },
    label: {
        paddingRight: 8,
        paddingLeft: 8
    },
    deleteIcon: {
        width: 14,
        height: 14,
        margin: 0,
        color: 'rgba(0, 0, 0, 0.54)'
    }
})(Chip)

const CustomFormControl = withStyles({
    root: {
        width: '100%'
    }
})(FormControl)

const CustomInput = withStyles({
    root: {
        'label + &': {
            marginTop: 0
        }
    }
})(Input)

const ChipsContainer = styled.div`
    display: flex;
    flex-wrap: wrap;
    overflow-x: auto;
    padding-bottom: 2px;
`

const PlaceHolderText = styled.div`
    color: rgba(0, 0, 0, 0.42);
    overflow: hidden;
`
